import React, { useState, useEffect } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence, useDragControls } from 'motion/react';
import { X, Minus, Maximize2, Minimize2 } from 'lucide-react';
import { macSpring, macWindowVariants } from '../../utils/macAnimations';

interface MacWindowProps {
  isOpen: boolean;
  onClose: () => void;
  title?: string;
  icon?: React.ReactNode;
  children: React.ReactNode;
  className?: string;
  maxWidth?: string;
}

export const MacWindow: React.FC<MacWindowProps> = ({
  isOpen,
  onClose,
  title,
  icon,
  children,
  className = "",
  maxWidth = "max-w-4xl"
}) => {
  const [isMinimized, setIsMinimized] = useState(false);
  const [isMaximized, setIsMaximized] = useState(false);
  const [isMounted, setIsMounted] = useState(false);
  const dragControls = useDragControls();

  useEffect(() => {
    setIsMounted(true);
  }, []);

  // Reset states when reopened
  useEffect(() => {
    if (isOpen) {
      setIsMinimized(false);
    }
  }, [isOpen]);

  // Close on Escape key
  useEffect(() => {
    if (!isOpen) return;
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isMounted || !isOpen) return null;

  return createPortal(
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.2 }}
          className="fixed inset-0 z-[99999] pointer-events-none flex items-center justify-center p-3 sm:p-4"
        >
          {/* Backdrop */}
          {!isMinimized && (
            <motion.div 
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              className="absolute inset-0 bg-zinc-950/75 backdrop-blur-xs pointer-events-auto cursor-pointer"
              onClick={onClose}
              title="Click backdrop to close"
            />
          )}

          {/* Window */}
          <motion.div
            layout
            drag={!isMaximized}
            dragControls={dragControls}
            dragListener={false}
            dragMomentum={false}
            variants={macWindowVariants}
            initial="initial"
            animate={{ 
              ...macWindowVariants.animate as any,
              y: isMinimized ? '45vh' : 0,
              x: 0,
              width: isMaximized ? '100vw' : 'auto',
              height: isMaximized ? '100vh' : 'auto',
              maxWidth: isMaximized ? '100vw' : undefined
            }}
            exit="exit"
            transition={macSpring}
            className={`pointer-events-auto bg-zinc-900/95 backdrop-blur-md shadow-2xl flex flex-col overflow-hidden border border-zinc-700/80 relative
              ${isMaximized ? 'rounded-none' : 'rounded-xl'}
              ${isMinimized ? 'w-64 h-12 shadow-lg !max-w-none' : `w-full ${maxWidth} max-h-[90vh] ${className}`}`}
            onClick={e => e.stopPropagation()}
            style={{ 
              originX: 0.5, 
              originY: 0.5 
            }}
          >
            {/* Mac Titlebar */}
            <div 
              className={`px-4 flex items-center shrink-0 bg-gradient-to-b from-zinc-800 to-zinc-900 border-b border-zinc-800/80
                ${isMinimized ? 'h-full cursor-pointer' : 'py-3'} ${!isMaximized ? 'cursor-move' : ''}`}
              onClick={() => isMinimized && setIsMinimized(false)}
              onPointerDown={(e) => {
                if (!isMaximized && !isMinimized) {
                  dragControls.start(e);
                }
              }}
            >
              {/* Traffic Lights */}
              <div className="flex items-center gap-2 mr-4">
                <button 
                  onClick={(e) => { e.stopPropagation(); onClose(); }}
                  className="w-3.5 h-3.5 rounded-full bg-red-500 hover:bg-red-600 shadow-inner flex items-center justify-center group transition-colors cursor-pointer"
                  title="Close Window (Esc)"
                >
                  <X className="w-2.5 h-2.5 text-red-950 opacity-0 group-hover:opacity-100" />
                </button>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    setIsMinimized(!isMinimized);
                    if (isMaximized) setIsMaximized(false);
                  }}
                  className="w-3.5 h-3.5 rounded-full bg-yellow-500 hover:bg-yellow-600 shadow-inner flex items-center justify-center group transition-colors cursor-pointer"
                  title={isMinimized ? "Restore Window" : "Minimize Window"}
                >
                  <Minus className="w-2.5 h-2.5 text-yellow-950 opacity-0 group-hover:opacity-100" />
                </button>
                <button 
                  onClick={(e) => { 
                    e.stopPropagation(); 
                    if (!isMinimized) setIsMaximized(!isMaximized);
                  }}
                  className={`w-3.5 h-3.5 rounded-full shadow-inner flex items-center justify-center group transition-colors cursor-pointer
                    ${isMinimized ? 'bg-zinc-700 cursor-not-allowed' : 'bg-green-500 hover:bg-green-600'}`}
                  title={isMaximized ? "Restore Size" : "Maximize Window"}
                >
                  {isMaximized ? (
                    <Minimize2 className="w-2.5 h-2.5 text-green-950 opacity-0 group-hover:opacity-100" />
                  ) : (
                    <Maximize2 className="w-2.5 h-2.5 text-green-950 opacity-0 group-hover:opacity-100" />
                  )}
                </button>
              </div>

              {/* Title Content */}
              <div className="flex-1 flex items-center justify-center pointer-events-none select-none">
                {title && (
                  <div className="flex items-center gap-2 text-zinc-300">
                    {icon && <span className="text-zinc-400">{icon}</span>}
                    <span className="text-sm font-semibold tracking-wide truncate text-zinc-100">{title}</span>
                  </div>
                )}
              </div>
              
              {/* Spacer on Right for Centered Title */}
              <div className="w-[52px] shrink-0" />
            </div>

            {/* Window Content */}
            <AnimatePresence>
              {!isMinimized && (
                <motion.div 
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  exit={{ opacity: 0 }}
                  className="flex-1 overflow-hidden flex flex-col relative bg-zinc-900"
                >
                  {children}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>,
    document.body
  );
};
